import fuzzysort from 'fuzzysort';

// The threshold for the fuzzy search. This is the default value of fuzzysort when searching for horses.
// Setting a score minimum of 0.8 to filter out low-quality matches
// Search for "Toon" will match mostly the actually name "toon" and not "toronto"
export const HORSE_FUZZY_SORT_THRESHOLD = 0.8;

/**
 * Abstract function to use the fuzzy search where we can put a list of keys and their weights.
 *
 * @param query string
 * @param items T[]
 * @param keys { key: string; weight: number }[]
 * @param threshold number
 * @returns Fuzzysort.KeysResults<T>
 * @example
 * const result = fuzzySearch<Horse>(
 *  'Toon',
 *  horses,
 *  keys: [
 *    { key: 'name', weight: 1 },
 *    { key: 'UELN', weight: 2 },
 *    { key: 'chip_nr', weight: 2 },
 *    { key: 'nickname', weight: 1 },
 *    { key: 'dam', weight: 1 },
 *    { key: 'damsire', weight: 1 },
 *    { key: 'sire', weight: 1 },
 *    { key: 'siredam', weight: 1 },
 *  ],
 *  0.5,
 * );
 */
export const fuzzySearch = <T extends object>(
  query: string,
  items: T[],
  keys: { key: string; weight: number }[],
  threshold: number,
): Fuzzysort.KeysResults<T> => {
  return fuzzysort.go<T>(query, items, {
    threshold,
    scoreFn: result => {
      // fetch the multipliers for each key
      const multipliers = keys.map(k => k.weight);

      // Sum all scores, applying the multiplier for each
      return result.reduce((total, { score }, index) => total + score * (multipliers[index] ?? 1), 0);
    },
    keys: keys.map(k => k.key),
  });
};
